import * as React from "react";

import {
  GenericFormConsumerInterface,
  GenericReactProviderFormInterfaceOutput,
  useCustomerCartId,
  useCustomerInformationQuery,
  useCustomerSession,
  useDataSourceAddressAutocompleteController,
  useDataSourceAddressAutocompleteQuery,
  useDataSourceAddressParseController,
  useDataSourceAddressParseQuery,
  useDataSourceCheckoutRegionsQuery,
  useForm,
  useLocalizationProvider,
} from "../../../Common";
import { useDataMutableSourceShippingClientStorageData } from "../data-mutable-source-shipping-client-storage-data-provider";
import { useDataMutableSourceShippingSessionStorageData } from "../data-mutable-source-shipping-session-storage-data-provider";
import { useMyBagDataSourceIntersection } from "../my-bag-provider";
import { useReactMyBagLoginApp } from "../react-my-bag-login-app-provider";
import { SendChangesToSession } from "./send-changes-to-session";

import {
  initialShippingForm,
  ShippingForm,
  ShippingFormApp,
  ShippingFormLogic,
} from "./shipping-form";
import {
  ShippingFormConsumerInterface,
  ShippingFormContext,
  ShippingFormControllerContext,
  ShippingForm_ControllerConsumerInterface,
} from "./use-shipping-form";

interface ShippingFormProviderProps {
  useInitialState: () => { initialState: ShippingFormApp["initialState"] };
}
export const ShippingFormProvider: React.FunctionComponent<
  ShippingFormProviderProps
> = (props) => {
  const { initialState } = props.useInitialState();

  const { countryCode } = useLocalizationProvider();

  const { data: checkoutRegions } = useDataSourceCheckoutRegionsQuery();

  const addressAutocomplete = useDataSourceAddressAutocompleteQuery();
  const addressAutocomplete_asController =
    useDataSourceAddressAutocompleteController();

  const addressParse = useDataSourceAddressParseQuery();
  const addressParse_asController = useDataSourceAddressParseController();

  const customerInformation = useCustomerInformationQuery();

  const shippingClientStorageData =
    useDataMutableSourceShippingClientStorageData();

  const reactMyBagLoginApp = useReactMyBagLoginApp();

  const myBagDataSource = useMyBagDataSourceIntersection();
  const customerSession = useCustomerSession();
  const customerCartId = useCustomerCartId();
  const shippingSessionStorageData =
    useDataMutableSourceShippingSessionStorageData();

  const appContext = React.useMemo(
    () => ({
      initialState,
      regions: checkoutRegions,
      countryCode,
      addressAutocomplete,
      addressAutocomplete_asController,
      addressParse,
      addressParse_asController,
      customerInformation,
      shippingClientStorageData,
      reactMyBagLoginApp,
      myBagDataSource,
      customerSession,
      customerCartId,
      shippingSessionStorageData,
    }),
    [
      initialState,
      checkoutRegions,
      countryCode,
      addressAutocomplete,
      addressAutocomplete_asController,
      addressParse,
      addressParse_asController,
      customerInformation,
      shippingClientStorageData,
      reactMyBagLoginApp,
      myBagDataSource,
      customerSession,
      customerCartId,
      shippingSessionStorageData,
    ]
  );
  const AppContext = React.useCallback(() => appContext, [appContext]);
  const thisForm = useForm(ShippingFormLogic, AppContext, initialShippingForm);
  const { form: shippingForm, validateRightNowAndGiveMeAYesOrNo } = thisForm;

  const value: ShippingFormConsumerInterface = React.useMemo(
    () => ({
      ...GenericReactProviderFormInterfaceOutput<ShippingForm, ShippingFormApp>(
        thisForm
      ),
    }),
    [thisForm]
  );

  const isTheShippingFormCompleteAndValid: ShippingForm_ControllerConsumerInterface["isTheShippingFormCompleteAndValid"] =
    React.useCallback(() => {
      return thisForm.validateRightNowAndGiveMeAYesOrNo();
    }, [thisForm]);

  const controllerValue: ShippingForm_ControllerConsumerInterface =
    React.useMemo(
      () => ({
        resetShippingForm: thisForm.resetForm,
        isTheShippingFormCompleteAndValid,
      }),
      [thisForm, isTheShippingFormCompleteAndValid]
    );

  return (
    <ShippingFormContext.Provider value={value}>
      <ShippingFormControllerContext.Provider value={controllerValue}>
        <SendChangesToSession>{props?.children || []}</SendChangesToSession>
      </ShippingFormControllerContext.Provider>
    </ShippingFormContext.Provider>
  );
};
